import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode, type FormEvent, type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { formOpened, formClosed } from '../platform';

/* ============================== icons ============================== */
/* Inline so there is no icon package to load, and no network request. Every one
   is sized by the global `svg` rule; overrides are per-context in CSS. */
const PATHS = {
  grid: 'M3 3h7.5v7.5H3zM13.5 3H21v7.5h-7.5zM3 13.5h7.5V21H3zM13.5 13.5H21V21h-7.5z',
  users: 'M9 11.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M3 20c0-3.8 2.7-6.5 6-6.5s6 2.7 6 6.5M17 9.3a2.3 2.3 0 1 0 0-4.6 2.3 2.3 0 0 0 0 4.6M15.3 13.6c2.4.4 4.2 2.6 4.2 5.9',
  briefcase: 'M3 7.5h18v12H3zM8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5M3 12.5h18',
  truck: 'M2.5 7h12v8.5h-12zM14.5 10h3.7L21 13v4.5h-6.5zM6.5 19.7a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4M17 19.7a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4',
  hardhat: 'M4 15.5C4 9.7 8.1 5 13 5s9 4.7 9 10.5M2.5 15.5h19v2.3a1.3 1.3 0 0 1-1.3 1.3H3.8a1.3 1.3 0 0 1-1.3-1.3zM13 5V2.8',
  box: 'M12 3 20 7.2v9.6L12 21 4 16.8V7.2zM4 7.2 12 11.4 20 7.2M12 11.4V21',
  bank: 'M3 9.5 12 4l9 5.5M4.5 9.5h15V19h-15zM4.5 19h15v1.8h-15zM8 9.5V19M12 9.5V19M16 9.5V19',
  repeat: 'M4 7h13l-2.7-2.7M17 7l-2.7 2.7M20 17H7l2.7 2.7M7 17l2.7-2.7',
  transfer: 'M12 21.3a9.3 9.3 0 1 0 0-18.6 9.3 9.3 0 0 0 0 18.6M8.5 15.5 15.5 8.5M12 8.5v7',
  filetext: 'M6.5 2.8h8L19 7.3v13.9H6.5zM14.5 2.8v4.5H19M8.7 12.2h6.6M8.7 15.5h6.6M8.7 18.8h4.2',
  inbox: 'M3.5 13.5h4l1.5 2.5h6l1.5-2.5h4M5.6 5h12.8l2.1 8.5v4a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5v-4z',
  shield: 'M12 3 20 6v5.5c0 4.6-3.2 8.3-8 9.5-4.8-1.2-8-4.9-8-9.5V6zM8.6 12.2l2.4 2.4 4.4-4.6',
  search: 'M10.6 17.2a6.6 6.6 0 1 0 0-13.2 6.6 6.6 0 0 0 0 13.2M20 20l-4.8-4.8',
  plus: 'M12 4.5v15M4.5 12h15',
  x: 'M5 5l14 14M19 5 5 19',
  check: 'M12 21.3a9.3 9.3 0 1 0 0-18.6 9.3 9.3 0 0 0 0 18.6M7.7 12.3l2.9 2.9 5.7-5.9',
  logout: 'M15 4.5h3.5a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5H15M10 16.5 14.5 12 10 7.5M14.5 12H4',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10M12 1.8v2.4M12 19.8v2.4M4.6 4.6l1.7 1.7M17.7 17.7l1.7 1.7M1.8 12h2.4M19.8 12h2.4M4.6 19.4l1.7-1.7M17.7 6.3l1.7-1.7',
  moon: 'M20.5 14.3A8.5 8.5 0 1 1 9.7 3.5a6.6 6.6 0 0 0 10.8 10.8',
  menu: 'M4 7h16M4 12h16M4 17h16',
  phone: 'M6.5 2.3h11v19.4h-11zM10.3 18.3h3.4',
  mail: 'M3 5.5h18v13H3zM3.6 6.5 12 13l8.4-6.5',
  pin: 'M12 21s6.5-6.1 6.5-11.2a6.5 6.5 0 0 0-13 0C5.5 14.9 12 21 12 21M12 12a2.3 2.3 0 1 0 0-4.6A2.3 2.3 0 0 0 12 12',
  wallet: 'M3 6.5h18v13H3zM3 10.5h18M16.5 15.8a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6',
  arrowleft: 'M19 12H5M11 6l-6 6 6 6',
  arrowright: 'M5 12h14M13 6l6 6-6 6',
  calendar: 'M4.5 6A1.3 1.3 0 0 1 5.8 4.7h12.4A1.3 1.3 0 0 1 19.5 6v13a1.3 1.3 0 0 1-1.3 1.3H5.8A1.3 1.3 0 0 1 4.5 19zM4.5 10h15M8.3 3v3.4M15.7 3v3.4',
  bell: 'M6 17V10.8a6 6 0 0 1 12 0V17l1.8 2.3H4.2zM10.3 20.8a1.9 1.9 0 0 0 3.4 0',
  filter: 'M3.5 5h17L14 13v6l-4 2v-8z',
  trash: 'M4.5 6.5h15M9.5 6.5V4.8a1.3 1.3 0 0 1 1.3-1.3h2.4a1.3 1.3 0 0 1 1.3 1.3v1.7M6.5 6.5v13a1.5 1.5 0 0 0 1.5 1.5h8a1.5 1.5 0 0 0 1.5-1.5v-13M10 10.5v6M14 10.5v6',
  edit: 'M4 20h4L18.3 9.7a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16zM13.8 6.4l3.8 3.8',
  clipboard: 'M8.5 4.5H7A1.5 1.5 0 0 0 5.5 6v14A1.5 1.5 0 0 0 7 21.5h10a1.5 1.5 0 0 0 1.5-1.5V6A1.5 1.5 0 0 0 17 4.5h-1.5M9 4.5V3.4A1.4 1.4 0 0 1 10.4 2h3.2A1.4 1.4 0 0 1 15 3.4v1.1zM9 11h6M9 14.5h4',
  sliders: 'M4 7h9M17 7h3M4 17h3M11 17h9M15 4.2v5.6M9 14.2v5.6',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 8.5v.1M12 11.5v4',
  layers: 'M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  database: 'M3 5c0-1.7 4-3 9-3s9 1.3 9 3v14c0 1.7-4 3-9 3s-9-1.3-9-3V5zm0 7c0 1.7 4 3 9 3s9-1.3 9-3M3 12c0 1.7 4 3 9 3s9-1.3 9-3',
  receipt: 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1zM8 7h8M8 11h8M8 15h5',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={PATHS[name]} />
    </svg>
  );
}

/* ============================== helpers ============================== */

export function initials(name: string): string {
  const clean = (name || '?').replace(/^(Mr\.|Mr|Mrs\.|Mrs|Ms\.|Ms|Dr\.|Dr|Er\.|Er)\s+/i, '').trim() || name || '?';
  return clean.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

/** dd MMM yyyy — the format used everywhere in the product. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_TONE: Record<string, string> = {
  ongoing: 'blue', planning: 'gray', on_hold: 'amber', completed: 'green',
  approved: 'green', pending: 'amber', rejected: 'red',
};
const STATUS_LABEL: Record<string, string> = {
  ongoing: 'Ongoing', planning: 'Planning', on_hold: 'On Hold', completed: 'Completed',
  approved: 'Approved', pending: 'Pending', rejected: 'Rejected',
};

export function StatusChip({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="chip chip-gray">—</span>;
  const tone = STATUS_TONE[value] ?? 'gray';
  return <span className={`chip chip-${tone}`}>{STATUS_LABEL[value] ?? value}</span>;
}

/* ============================== toasts ============================== */

interface ToastAction { label: string; run: () => void | Promise<void> }
interface Toast { id: number; message: string; bad?: boolean; action?: ToastAction }

interface ToastApi {
  show: (message: string, bad?: boolean) => void;
  /**
   * A toast carrying a way back. Used for deletion: the realistic mistake is
   * clicking the wrong row, and the realistic moment to notice is the next two
   * seconds — so undo belongs here, not buried in a recycle bin.
   */
  showWithAction: (message: string, action: ToastAction) => void;
}

const ToastCtx = createContext<ToastApi>({ show: () => {}, showWithAction: () => {} });
export const useToast = () => useContext(ToastCtx);

/** Undo stays available noticeably longer than a plain toast. */
const PLAIN_MS = 3200;
const ACTION_MS = 9000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((t: Omit<Toast, 'id'>, ms: number) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { ...t, id }]);
    setTimeout(() => dismiss(id), ms);
  }, [dismiss]);

  const show = useCallback((message: string, bad = false) => {
    push({ message, bad }, PLAIN_MS);
  }, [push]);

  const showWithAction = useCallback((message: string, action: ToastAction) => {
    push({ message, action }, ACTION_MS);
  }, [push]);

  const api = useMemo(() => ({ show, showWithAction }), [show, showWithAction]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={t.bad ? 'toast bad' : 'toast'}>
            <span>{t.message}</span>
            {t.action && (
              <button
                onClick={() => { void t.action?.run(); dismiss(t.id); }}
                style={{
                  marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
                  color: '#fff', fontFamily: 'var(--font-ui)', fontWeight: 700,
                  fontSize: 12.5, textDecoration: 'underline', padding: '0 0 0 14px',
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ============================== drawer ============================== */

export function Drawer({
  title, description, onClose, onSubmit, submitLabel = 'Save', busy, onDelete, wide, style, children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  submitLabel?: string;
  busy?: boolean;
  /** For forms with a line table — a bill is unreadable at 540px. */
  wide?: boolean;
  style?: CSSProperties;
  /**
   * Shown at the left of the footer when editing. Deleting belongs next to the
   * record being changed — a separate bin icon in a crowded ledger row is easy
   * to hit by accident.
   */
  onDelete?: () => void;
  children: ReactNode;
}) {
  // Escape closes, and the page behind must not scroll while it is open.
  // Also marks a drawer as open so a pending PWA update waits until this
  // closes rather than reloading out from under someone mid-entry.
  useEffect(() => {
    formOpened();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      formClosed();
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <aside className={wide ? 'drawer drawer-wide' : 'drawer'}
             style={style}
             role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <div>
            <h3>{title}</h3>
            {description && <div className="desc">{description}</div>}
          </div>
          <button type="button" className="icon-btn" style={{ marginLeft: 'auto' }}
                  onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="drawer-body">{children}</div>
          <div className="drawer-foot">
            {onDelete && (
              <button type="button" className="btn btn-auto btn-ghost btn-danger"
                      style={{ marginRight: 'auto' }} onClick={onDelete} disabled={busy}>
                <Icon name="trash" /> Delete
              </button>
            )}
            <button type="button" className="btn btn-auto" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-auto" disabled={busy}>
              {busy ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </aside>
    </>,
    document.body,
  );
}

/* ============================== form field ============================== */

export function Field({
  label, error, hint, optional, span2, children,
}: {
  label: string; error?: string; hint?: string; optional?: boolean;
  span2?: boolean; children: ReactNode;
}) {
  return (
    <div className={span2 ? 'field span2' : 'field'}>
      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, whiteSpace: 'nowrap' }}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        {optional && <span className="opt" style={{ flexShrink: 0, fontWeight: 400, color: 'var(--text-faint)', fontSize: 11 }}> (optional)</span>}
      </label>
      {children}
      {error ? <div className="err">{error}</div> : hint ? <div className="err" style={{ color: 'var(--text-faint)' }}>{hint}</div> : null}
    </div>
  );
}

/* ============================== states ============================== */

export function EmptyState({ icon, title, hint }: { icon: IconName; title: string; hint?: string }) {
  return (
    <div className="empty">
      <Icon name={icon} />
      <div className="t">{title}</div>
      {hint && <div>{hint}</div>}
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, c) => (
              <th key={c}><div className="skeleton" style={{ width: '60px', height: '11px' }} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><div className="skeleton" style={{ width: c === 0 ? '75%' : c === 1 ? '55%' : '45%', height: '13px' }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function KpiSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="kpi-row">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="kpi">
          <div className="skeleton" style={{ width: '40%', height: '11px', marginBottom: '10px' }} />
          <div className="skeleton" style={{ width: '70%', height: '24px', marginBottom: '6px' }} />
          <div className="skeleton" style={{ width: '50%', height: '11px' }} />
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div className="card">
      <div className="card-head">
        <div className="skeleton" style={{ width: '140px', height: '16px' }} />
      </div>
      <div className="card-body">
        <div className="skeleton" style={{ width: '100%', height: '14px', marginBottom: '10px' }} />
        <div className="skeleton" style={{ width: '90%', height: '14px', marginBottom: '10px' }} />
        <div className="skeleton" style={{ width: '75%', height: '14px' }} />
      </div>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <div className="empty">
      <div className="t" style={{ color: 'var(--red)' }}>Could not load this</div>
      <div style={{ marginBottom: 12 }}>{message}</div>
      {onRetry && <button className="btn btn-auto btn-sm" onClick={onRetry}>Try again</button>}
    </div>
  );
}

export { default as Select, type SelectOption, type SelectProps } from './Select';

